import { AgGridModule } from '@ag-grid-community/angular';
import { GridApi, GridReadyEvent } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { reduce } from 'lodash-es';

import { VISIT_COST_OPTION } from '@features/patient-group-dropdown/constants/visit-cost-option.const';

import { injectPatientInvoiceables } from '@models/patient-protocol/api/patient-invoiceables.api';
import { injectPatientVisits } from '@models/patient-protocol/api/patient-visits.api';
import { injectTrialSitePaymentSchedules } from '@models/sites/api/site-budget.api';
import { getPatientProtocolTypes } from '@models/sites/api/sites.api';
import { SiteCostType } from '@models/sites/site-cost.enum';
import { SitesQuery } from '@models/sites/sites.query';

import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { Currency } from '@shared/services/gql.service';
import { CurrencyToggle } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { siteCostSummaryTableConfig } from './grid-config/site-cost-summary-table-config';
import {
  AggregateCostKey,
  PaymentSchedule,
  SiteSummaryGridRow,
} from './types/site-summary-grid-row';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridModule, TableSkeletonComponent],
  selector: 'aux-site-cost-summary-table',
  styleUrls: ['./site-cost-summary-table.component.scss'],
  templateUrl: './site-cost-summary-table.component.html',
})
export class SiteCostSummaryTableComponent {
  siteQuery = inject(SitesQuery);

  costType = input.required<SiteCostType>();
  protocolVersionId = input.required<null | string>();

  patientInvoiceables = injectPatientInvoiceables({
    enabled: computed(() => this.costType() === SiteCostType.PATIENT_INVOICEABLES),
    protocolVersionId: this.protocolVersionId,
  });
  patientVisits = injectPatientVisits({
    enabled: computed(() => this.costType() === SiteCostType.PATIENT_VISITS),
    protocolVersionId: this.protocolVersionId,
  });

  isPatientVisitCost = computed(() => this.costType() === SiteCostType.PATIENT_VISITS);
  patientProtocolTypes = computed(() => getPatientProtocolTypes(this.costType));

  patientGroupId = input.required<null | string>();
  siteIds = input.required<string[]>();

  paymentSchedules = injectTrialSitePaymentSchedules(
    computed(() => ({
      patient_group_id: this.isPatientVisitCost()
        ? this.patientGroupId() === 'visits_costs'
          ? null
          : this.patientGroupId()
        : null,
      patient_protocol_types: this.patientProtocolTypes(),
      patient_protocol_version_id: this.protocolVersionId(),
      site_ids: this.siteIds(),
    })),
    computed(() => {
      const patientGroupCheck = this.isPatientVisitCost() ? !!this.patientGroupId() : true;

      return !!this.protocolVersionId() && !!this.siteIds().length && patientGroupCheck;
    })
  );

  gridAPI = signal<GridApi<SiteSummaryGridRow> | null>(null);
  siteExpanded = signal(true);

  gridData = computed(() => {
    const gridData =
      this.costType() === SiteCostType.SITE_INVOICEABLES
        ? this.getSiteInvoiceableGridData()
        : this.getPatientVisitGridData();

    return gridData;
  });
  isAnySiteHasNonUsdCurrency = computed(() => {
    return this.gridData().some(({ contractedCurrency }) => contractedCurrency !== Currency.USD);
  });

  selectedCurrency = input.required<CurrencyToggle>();

  isSelectedCurrencyContracted = computed(
    () => this.selectedCurrency() === CurrencyToggle.CONTRACTED
  );

  afterSitesExpandToggle = (expand: boolean) => {
    this.siteExpanded.set(expand);
  };

  gridOptions = computed(() => {
    return siteCostSummaryTableConfig({
      afterSitesExpandToggle: this.afterSitesExpandToggle,
      aggregateColumns: this.getAggregateColumns(),
      isAnySiteHasNonUsdCurrency: this.isAnySiteHasNonUsdCurrency(),
      isSelectedCurrencyContracted: this.isSelectedCurrencyContracted(),
      isSiteExpanded: this.siteExpanded(),
      siteCostType: this.costType(),
    });
  });
  isProcessing = computed(
    () =>
      this.patientInvoiceables.isFetching() ||
      this.patientVisits.isFetching() ||
      this.paymentSchedules.isFetching()
  );

  _sizeColumnsToFit = effect(() => {
    this.costType();
    const DELAY = 100;

    setTimeout(() => {
      this.gridAPI()!.sizeColumnsToFit();
    }, DELAY);
  });
  _syncGridOptions = effect(() => {
    // any changes to the grid options will trigger a re-render
    const newGridOptions = this.gridOptions();

    // don't track the gridAPI
    const gridAPI = untracked(() => this.gridAPI());

    if (!gridAPI) {
      return;
    }

    Object.entries(newGridOptions).forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gridAPI.setGridOption(key as any, value);
    });
  });

  private createEmptySiteSummaryRow(siteId: string): SiteSummaryGridRow {
    const site = this.siteQuery.getEntity(siteId);

    return {
      contractedCurrency: Currency.USD,
      effectiveDate: '',
      frequencies: {} as AggregateCostKey<
        'Activation' | 'Annually' | 'Closeout' | 'Monthly' | 'Quarterly' | 'Uncategorized'
      >,
      id: site?.id ?? '',
      siteBudgetVersionName: '',
      siteName: site?.name ?? '',
      siteNo: site?.site_no ?? '',
      totalAmount: 0,
      totalAmountContracted: 0,
      visitCosts: {} as AggregateCostKey,
    };
  }

  private ensureAllSitesHaveRows(
    rows: SiteSummaryGridRow[],
    siteIds: string[]
  ): SiteSummaryGridRow[] {
    const existingSiteIds = new Set(rows.map((row) => row.id));

    const missingSiteRows = siteIds
      .filter((siteId) => !existingSiteIds.has(siteId))
      .map((siteId) => this.createEmptySiteSummaryRow(siteId));

    return [...rows, ...missingSiteRows].sort((a, b) =>
      Utils.localeAlphaNumSort(a.siteNo, b.siteNo)
    );
  }

  getAggregateColumns() {
    let aggregateColumns: { field: string; name: string }[] = [];

    if (this.costType() !== SiteCostType.SITE_INVOICEABLES) {
      const patientVisitsData =
        this.costType() === SiteCostType.PATIENT_VISITS
          ? this.getPatientVisitColumns()
          : this.patientInvoiceables.data();

      aggregateColumns = (patientVisitsData || []).map((visit) => ({
        field: visit.id,
        name: visit.name,
        optional: 'optional' in visit ? visit.optional : false,
      }));
    }

    return aggregateColumns;
  }

  private getBaseSiteSummaryRow(
    firstVisit: PaymentSchedule
  ): Pick<
    SiteSummaryGridRow,
    'contractedCurrency' | 'effectiveDate' | 'id' | 'siteBudgetVersionName' | 'siteName' | 'siteNo'
  > {
    const {
      expense_amount,
      site_budget_version_effective_date,
      site_budget_version_name,
      site_id,
    } = firstVisit;

    const site = this.siteQuery.getEntity(site_id);
    const contractCurrency = (expense_amount.contract_curr as Currency) || Currency.USD;

    return {
      contractedCurrency: contractCurrency,
      effectiveDate: site_budget_version_effective_date ?? '',
      id: site?.id ?? '',
      siteBudgetVersionName: site_budget_version_name ?? '',
      siteName: site?.name ?? '',
      siteNo: site?.site_no ?? '',
    };
  }

  getGroupedPaymentSchedules() {
    const paymentSchedules = this.paymentSchedules.data() || [];

    return reduce(
      paymentSchedules,
      (result, schedule) => {
        const key = `${schedule.site_id}-${schedule.site_budget_version_id}`;

        (result[key] || (result[key] = [])).push(schedule);

        return result;
      },
      {} as Record<string, typeof paymentSchedules>
    );
  }

  getPatientVisitColumns() {
    return (this.patientVisits.data() || []).filter(({ patient_group_id }) => {
      return this.patientGroupId() === VISIT_COST_OPTION.value
        ? patient_group_id === null
        : patient_group_id === this.patientGroupId();
    });
  }

  getPatientVisitGridData(): SiteSummaryGridRow[] {
    const groupedPaymentSchedules = this.getGroupedPaymentSchedules();

    const visitsSet = new Set(this.getPatientVisitColumns().map((col) => col.id));

    const rows = Object.values(groupedPaymentSchedules).map((visits) => {
      const patientVisitFilteredSchedules =
        this.costType() === SiteCostType.PATIENT_VISITS
          ? visits.filter((visit) => visitsSet.has(visit.patient_protocol.id))
          : visits;

      const visitCosts = this.processVisitPaymentSchedules(patientVisitFilteredSchedules);
      const siteSummary = this.getBaseSiteSummaryRow(visits[0]);
      const totalAmount = this.getTotalAmount(visitCosts, siteSummary.contractedCurrency);

      return {
        ...siteSummary,
        visitCosts,
        ...totalAmount,
      };
    });

    return this.ensureAllSitesHaveRows(rows, this.siteIds());
  }

  getSiteInvoiceableGridData(): SiteSummaryGridRow[] {
    const groupedPaymentSchedules = this.getGroupedPaymentSchedules();
    const rows = Object.values(groupedPaymentSchedules).map((visits) => {
      const siteSummary = this.getBaseSiteSummaryRow(visits[0]);
      const frequencies = this.processInvoiceableFrequencies(visits);

      const totalAmount = this.getTotalAmount(frequencies, siteSummary.contractedCurrency);

      return {
        ...siteSummary,
        frequencies: this.processInvoiceableFrequencies(visits),
        ...totalAmount,
      };
    });

    return this.ensureAllSitesHaveRows(rows, this.siteIds());
  }

  getTotalAmount(
    visits: AggregateCostKey<string>,
    contractedCurrency: Currency
  ): {
    totalAmount: number;
    totalAmountContracted: number;
  } {
    return Object.values(visits).reduce(
      (acc, curr) => {
        return {
          totalAmount: acc.totalAmount + (curr[Currency.USD] || 0),
          totalAmountContracted: acc.totalAmountContracted + (curr[contractedCurrency] || 0),
        };
      },
      {
        totalAmount: 0,
        totalAmountContracted: 0,
      }
    );
  }

  onGridReady(event: GridReadyEvent) {
    this.gridAPI.set(event.api);
    this.gridAPI()!.sizeColumnsToFit();
  }

  private processInvoiceableFrequencies(visits: PaymentSchedule[]): AggregateCostKey<string> {
    return visits.reduce<AggregateCostKey<string>>((acc, visit) => {
      const { expense_amount, patient_protocol } = visit;
      const { amount, amount_curr, contract_amount, contract_curr } = expense_amount;

      acc[patient_protocol.name] = {
        [amount_curr as Currency]: amount || 0,
        [contract_curr as Currency]: contract_amount || 0,
      };

      return acc;
    }, {});
  }

  private processVisitPaymentSchedules(visits: PaymentSchedule[]): AggregateCostKey {
    return visits.reduce((acc, visit) => {
      const { amount, amount_curr, contract_amount, contract_curr } = visit.expense_amount;

      acc[visit.patient_protocol.id] = {
        [amount_curr as Currency]: amount,
        [contract_curr as Currency]: contract_amount,
      };

      return acc;
    }, {} as AggregateCostKey);
  }
}
